import { numberWithCommas } from "@/utils";
import { Typography, Tooltip } from "@mui/material";
import { HTSCodeWithTooltip } from "@/components";

export const columns = (): IColumn<ITariff & { percentOfTotal?: number; effectiveDutyRate?: number }>[] => [
  {
    name: "HTS Code",
    key: "htsNumber",
    align: "left",
    width: "28%",  // Increased from 25%
    minWidth: "110px",
    component: (data) => {
      const htsCode = data.htsNumber == null ? "" : String(data.htsNumber);
      
      const cell = (
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "8px", md: "9px", lg: "10px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: htsCode ? "pointer" : "default",
            ...(htsCode && {
              "&:hover": {
                color: "#0b494b",
                textDecoration: "underline",
              },
            }),
          }}
        >
          {htsCode || "—"}
        </Typography>
      );

      // Only wrap with tooltip behavior when we have a valid HTS code
      return htsCode ? (
        <HTSCodeWithTooltip htsCode={htsCode}>{cell}</HTSCodeWithTooltip>
      ) : (
        cell
      );
    },
  },
  {
    name: "Goods Value",
    key: "tariffValue",
    align: "right",
    width: "18%",  // Slightly reduced from 19%
    minWidth: "65px",
    component: ({ tariffValue }) => {
      const label = `$${numberWithCommas(tariffValue || 0)}`;
      return (
        <Tooltip title={label} arrow>
          <Typography variant="body2" sx={{ fontSize: { xs: '9px', md: '10px', lg: '11px' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    name: "Customs Duty",
    key: "tariffDuty",
    align: "right",
    width: "18%",  // Slightly reduced from 19%
    minWidth: "65px",
    component: ({ tariffDuty }) => {
      const label = `$${numberWithCommas(tariffDuty || 0)}`;
      return (
        <Tooltip title={label} arrow>
          <Typography variant="body2" sx={{ fontSize: { xs: '9px', md: '10px', lg: '11px' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    name: "Effective Duty Rate",
    key: "effectiveDutyRate",
    align: "right",
    width: "21%",  // Slightly reduced from 22%
    minWidth: "80px",
    component: (data) => {
      const tariffValue = data.tariffValue || 0;
      const tariffDuty = data.tariffDuty || 0;
      const rate = tariffValue > 0 ? (tariffDuty / tariffValue) * 100 : 0;
      const color = rate < 5 ? "#2E7D32" : rate < 10 ? "#ED6C02" : "#D32F2F";
      const backgroundColor = rate < 5 ? "#E8F5E9" : rate < 10 ? "#FFF3E0" : "#FFEBEE";
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            color,
            backgroundColor,
            fontWeight: 700,
            padding: { xs: "1px 3px", md: "2px 4px" },
            borderRadius: "4px",
            display: "inline-block",
            fontSize: { xs: "8px", md: "9px", lg: "10px" }
          }}
        >
          {rate.toFixed(2)}%
        </Typography>
      );
    },
  },
  {
    name: "% of Total",
    key: "percentOfTotal",
    align: "right",
    width: "15%",
    minWidth: "55px",
    component: (data) => {
      const percent = ((data as any).percentOfTotal || 0).toFixed(2);
      return (
        <Typography variant="body2" sx={{ fontSize: { xs: '9px', md: '10px', lg: '11px' } }}>
          {percent}%
        </Typography>
      );
    },
  },
];

// Export the original columns function for backward compatibility
export const columnsOriginal: IColumn<ITariff & { percentOfTotal?: number; effectiveDutyRate?: number }>[] = columns();